import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/set'
import { useMemo } from 'react'

import { gql } from '@/clients/utoniq-core'
import {
  ArtistExportPatronsCsvQuery,
  ArtistExportPatronsCsvQueryVariables,
  ConsoleMembershipQuery,
  ConsoleMembershipQueryVariables,
  ConsolePlanDetailFragmentDoc,
  ConsoleProductDetailFragment,
  ConsoleProductDetailFragmentDoc,
  MembershipInterval,
  UpdateSubscriptionRequiredPersonalInformationMutation,
  UpdateSubscriptionRequiredPersonalInformationMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useMessage, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { useDownloadFile } from '@/lib/downloadFile'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { showError } = useMessage()
  const { t } = useTranslation()
  const { downloadCsv } = useDownloadFile()
  const { data, loading } = useQuery<
    ConsoleMembershipQuery,
    ConsoleMembershipQueryVariables
  >(QUERY, {
    variables: { artistId: user?.uid },
    skip: !user?.uid,
  })

  const [artistExportPatronsCsv] = useLazyQuery<
    ArtistExportPatronsCsvQuery,
    ArtistExportPatronsCsvQueryVariables
  >(ARTIST_EXPORT_PATRONS_CSV, {
    async onCompleted({ artistExportPatronsCsv }) {
      if (!artistExportPatronsCsv?.csvUrl) {
        return showError(new Error(t('fileNotSaved')))
      }
      await downloadCsv(artistExportPatronsCsv.csvUrl)
    },
  })

  const [updateSubscriptionRequirePersonalInformation, {}] = useMutation<
    UpdateSubscriptionRequiredPersonalInformationMutation,
    UpdateSubscriptionRequiredPersonalInformationMutationVariables
  >(UPDATE_SUBSCRIPTION_REQUIRED_PERSONAL_INFO)

  const products = useMemo<ConsoleProductDetailFragment[]>(() => {
    if (data?.artist?.subscription?.products?.length) {
      return data.artist.subscription.products
    }
    if (data?.artist?.subscription?.plans?.length) {
      return data?.artist?.subscription.plans.map(plan => ({
        title: plan.title,
        content: plan.content,
        annualContent: null,
        productId: null,
        plans: [{ ...plan, interval: MembershipInterval.Month }],
      }))
    }
    return []
  }, [data?.artist?.subscription?.plans, data?.artist?.subscription?.products])

  return {
    state: {
      artistId: user?.uid ?? '',
      currentUser: user,
      artist: data?.artist ?? null,
      products,
      loading,
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {
      async onExportCsv() {
        const productIds = products
          .map(product => product.productId ?? '')
          .filter(productId => !!productId)
        await artistExportPatronsCsv({ variables: { productIds } })
      },
      async onChangeRequirePersonalInformation() {
        if (!data?.artist) return
        const artistClone = cloneDeep(data.artist) || {}
        await updateSubscriptionRequirePersonalInformation({
          variables: {
            isRequired: !data.artist.subscription?.requirePersonalInformation,
          },
          optimisticResponse({ isRequired }) {
            set(
              artistClone,
              'subscription.requirePersonalInformation',
              isRequired
            )
            return {
              updateSubscriptionRequirePersonalInformation: {
                ...artistClone,
              },
            }
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleMembership($artistId: ID) {
    artist(id: $artistId) {
      id
      paymentType
      subscription {
        products {
          productId
        }
      }
      hasArrangingDeliveryItems
      subscription {
        requirePersonalInformation
        plans {
          ...ConsolePlanDetail
        }
        products {
          ...ConsoleProductDetail
        }
      }
    }
  }
  ${ConsolePlanDetailFragmentDoc}
  ${ConsoleProductDetailFragmentDoc}
`

const ARTIST_EXPORT_PATRONS_CSV = gql`
  query ArtistExportPatronsCsv($productIds: [String!]!) {
    artistExportPatronsCsv(productIds: $productIds) {
      csvUrl
    }
  }
`

const UPDATE_SUBSCRIPTION_REQUIRED_PERSONAL_INFO = gql`
  mutation UpdateSubscriptionRequiredPersonalInformation(
    $isRequired: Boolean!
  ) {
    updateSubscriptionRequirePersonalInformation(isRequired: $isRequired) {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        requirePersonalInformation
        plans {
          ...ConsolePlanDetail
        }
        products {
          ...ConsoleProductDetail
        }
      }
    }
  }
  ${ConsolePlanDetailFragmentDoc}
  ${ConsoleProductDetailFragmentDoc}
`
